<template>
  <button 
    class="button" 
    :class="[
      `button--${variant}`,
      { 
        'button--with-icon': icon,
        'button--danger': danger,
        'button--no-background': noBackground,
        'button--success': variant === 'success',
        'button--mobile': mobile,
        'button--loading': loading
      }
    ]"
    :disabled="loading || disabled"
    v-bind="$attrs"
  >
    <Icon 
      v-if="icon && !loading"
      :name="icon"
      :size="16"
      class="button__icon"
      :interactive="false"
    />
    <v-progress-circular
      v-if="loading"
      indeterminate
      size="16"
      width="2"
      color="currentColor"
      class="button__loader"
    />
    <span class="button__text">
      <slot></slot>
    </span>
  </button>
</template>

<script setup lang="ts">
import Icon from '@/components/icons/Icon.vue'

defineProps<{
  variant?: 'primary' | 'secondary' | 'tertiary' | 'outline' | 'ghost' | 'success'
  icon?: string
  danger?: boolean
  noBackground?: boolean
  mobile?: boolean
  loading?: boolean
  disabled?: boolean
}>()
</script>

<style scoped>
.button {
  display: flex;
  padding: 0px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 6px;
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.14px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 40px;
}

.button.button--mobile {
  height: 44px;
}

/* Primary variant */
.button--primary {
  background: var(--components-primary-button);
  color: var(--components-on-primary-button);
  border: 1px solid var(--layout-border-primary);
}

.button--primary:hover {
  background: var(--components-primary-button-hover);
}

.button--primary.button--danger {
  background: var(--error);
  border-color: var(--error-hover);
}

.button--primary.button--danger:hover {
  background: var(--error-hover);
}

.button--primary.button--danger:disabled {
  background: var(--error-surface);
  border-color: var(--error-surface);
  color: var(--error-hover);
  cursor: not-allowed;
}

.button--primary.button--danger:disabled :deep(svg) {
  color: var(--error-hover);
}

.button--primary :deep(svg) {
  color: white;
}

/* Secondary variant */
.button--secondary {
  background: var(--layout-surface);
  color: var(--text-secondary);
  border: 1px solid var(--layout-border);
}

.button--secondary:hover {
  background: var(--layout-surface-variant);
  color: var(--text-defaut-emphasis);
}

.button--secondary.button--danger {
  background: var(--error-surface); /* Fond rouge clair */
  color: var(--error);
  border-color: var(--error);
}

.button--secondary.button--danger:hover {
  background: var(--error-surface);
  color: var(--error-hover);
  border-color: var(--error-hover);
}

.button--secondary.button--danger :deep(svg) {
  color: var(--error);
}

.button--secondary :deep(svg) {
  color: var(--components-icon-color);
}

/* Tertiary variant */
.button--tertiary {
  background: var(--layout-surface);
  color: var(--text-secondary);
  border: none;
}

.button--tertiary:hover {
  background: var(--layout-surface-variant);
  color: var(--text-defaut-emphasis);
}

.button--tertiary.button--no-background {
  background: transparent;
}

.button--tertiary.button--no-background:hover {
  background: transparent;
}

.button--tertiary.button--danger {
  color: var(--error);
  background: none;
}

.button--tertiary.button--danger:hover {
  background: var(--error-surface);
  color: var(--error-hover);
}

.button--tertiary.button--danger :deep(svg) {
  color: var(--error);
}

.button--tertiary :deep(svg) {
  color: var(--components-icon-color);
}

/* Outline variant */
.button--outline {
  background: transparent;
  color: var(--text-defaut);
  border: 1px solid var(--layout-border);
}

.button--outline:hover {
  border-color: var(--layout-border-emphasis);
  color: var(--text-defaut-emphasis);
}

/* Ghost variant */
.button--ghost {
  background: transparent;
  color: var(--text-defaut);
  border: none;
}

.button--ghost:hover {
  background: var(--layout-surface-variant);
  color: var(--text-defaut-emphasis);
}

.button--ghost.button--danger {
  color: var(--error);
}

.button--ghost.button--danger:hover {
  background: var(--error-surface);
  color: var(--error-hover);
}

.button--ghost.button--danger :deep(svg) {
  color: var(--error);
}

/* Success variant */
.button--success {
  background: var(--success);
  color: white;
}

.button--success:hover {
  background: var(--success-hover);
}

.button--success:active {
  background: var(--success-active);
}

/* Loading variant */
.button--loading {
  cursor: wait;
}

.button__loader {
  margin-right: 8px;
}

/* Icon styles */
.button__icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

/* Disabled state */
.button:disabled {
  cursor: not-allowed;
  pointer-events: none;
}

.button--primary:disabled {
  background: var(--layout-surface-variant);
  border-color: var(--layout-border);
  color: var(--text-placeholder);
}

.button--primary:disabled :deep(svg) {
  color: var(--components-icon-ui-element);
}

.button--secondary:disabled {
  background: var(--layout-surface);
  color: var(--text-secondary);
  border-color: var(--layout-border);
}

.button--tertiary:disabled {
  background: var(--layout-surface);
  color: var(--text-secondary);
}

.button--outline:disabled {
  border-color: var(--layout-border);
  color: var(--text-secondary);
}

.button--ghost:disabled {
  color: var(--text-secondary);
}
</style>
